import ArrayUtils from "./ArrayUtils.js";
import { buildQuiz, buildProposals } from "./DailyQuestions.js";
import { populationRank } from "./QuestionWarmup.js";
import { CONTINENTS } from "./Continents.js";

/**
 * Questions du Défi un ami.
 *
 * Les thèmes d'origine (drapeaux, capitales, et « les deux » des anciens liens)
 * passent tels quels par `buildQuiz`, le générateur du Défi du jour : un lien
 * déjà envoyé doit redonner exactement les mêmes questions.
 *
 * Les thèmes ajoutés ensuite ont leur propre tirage, tout aussi déterministe
 * (même graine, mêmes questions, dans les deux langues) :
 * - « borders » : un pays (drapeau et nom), et quatre pays dont un seul est
 *   son voisin ; les trois autres viennent du même continent, sinon l'intrus
 *   se repère sans rien savoir de ses frontières ;
 * - « shapes » : une silhouette et quatre pays du même continent ;
 * - « all » : drapeaux, capitales, frontières et silhouettes mêlés.
 */

export const LEGACY_THEMES = ["flags", "capitals", "mix"];
export const NEW_THEMES = ["borders", "shapes", "all"];

/** Types de question de chaque thème ajouté. */
const THEME_TYPES = {
	borders: ["border"],
	shapes: ["shape"],
	all: ["flag", "capital", "border", "shape"],
};

/** Le thème a-t-il besoin des frontières ou des silhouettes ? */
export const needsBorders = (theme) => theme === "borders" || theme === "all";
export const needsShapes = (theme) => theme === "shapes" || theme === "all";

/**
 * @param {object} args
 * @param {Array<{code: string, name: string, capital: string}>} args.countries  les 193
 * @param {number} args.seed
 * @param {string} args.theme
 * @param {Array<{maxRank: number, count: number}>} args.tiers
 * @param {Object<string, string[]>} [args.borders]  voisins par code (country-details.json)
 * @param {Set<string>} [args.shapeCodes]  pays qui ont une silhouette
 * @returns {Array<{country: object, type: string, answer: string, proposals: object[]}>}
 */
export function buildChallenge({ countries, seed, theme, tiers, borders = {}, shapeCodes = new Set() }) {
	if (!THEME_TYPES[theme]) {
		return buildQuiz({ countries, seed, mode: "classic", theme, tiers })
			.map(q => ({ ...q, answer: q.country.code }));
	}

	const canonical = [...countries].sort((a, b) => (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
	const byCode = new Map(canonical.map(c => [c.code, c]));
	const neighboursOf = (code) => [...new Set(borders[code] || [])].filter(c => byCode.has(c)).sort();
	const eligible = {
		flag: () => true,
		capital: () => true,
		border: (c) => neighboursOf(c.code).length > 0,
		shape: (c) => shapeCodes.has(c.code),
	};

	const shuffled = ArrayUtils.seededShuffle(canonical, seed);
	const rng = ArrayUtils.seededRandom(seed + 2000);
	const total = tiers.reduce((sum, tier) => sum + tier.count, 0);

	// Types à parts égales, dans un ordre tiré au sort.
	const cycle = THEME_TYPES[theme];
	const types = Array.from({ length: total }, (_, i) => cycle[i % cycle.length]);
	shuffleInPlace(types, rng);

	const used = new Set();
	const questions = [];
	let index = 0;
	let minRank = 0;
	tiers.forEach((tier, level) => {
		for (let k = 0; k < tier.count; k++, index++) {
			const type = types[index];
			const inTier = (c) => {
				const rank = populationRank(c.code);
				return rank >= minRank && rank < tier.maxRank;
			};
			// Le palier si possible, sinon n'importe quel pays encore libre : un
			// palier pauvre en voisins ou en silhouettes ne raccourcit pas le défi.
			const country = shuffled.find(c => !used.has(c.code) && eligible[type](c) && inTier(c))
				|| shuffled.find(c => !used.has(c.code) && eligible[type](c));
			if (!country) continue;
			used.add(country.code);
			questions.push(buildQuestion({ country, type, level, canonical, byCode, neighboursOf, shapeCodes, rng }));
		}
		minRank = tier.maxRank;
	});
	return questions;
}

function buildQuestion({ country, type, level, canonical, byCode, neighboursOf, shapeCodes, rng }) {
	if (type === "flag" || type === "capital") {
		return { country, type, answer: country.code, proposals: buildProposals(country, type, level, canonical, rng) };
	}
	const entry = (c) => ({ code: c.code, name: c.name });
	const zone = CONTINENTS[country.code];
	if (type === "shape") {
		const pool = canonical.filter(c => shapeCodes.has(c.code) && c.code !== country.code);
		const proposals = [entry(country), ...pickDistractors(pool, zone, rng)];
		shuffleInPlace(proposals, rng);
		return { country, type, answer: country.code, proposals };
	}
	// Frontière : un voisin à trouver parmi des pays qui n'en sont pas.
	const neighbours = neighboursOf(country.code);
	const answer = byCode.get(neighbours[Math.floor(rng() * neighbours.length)]);
	const pool = canonical.filter(c => c.code !== country.code && !neighbours.includes(c.code));
	const proposals = [entry(answer), ...pickDistractors(pool, zone, rng)];
	shuffleInPlace(proposals, rng);
	return { country, type, answer: answer.code, proposals };
}

/** Trois distracteurs, du même continent d'abord. */
function pickDistractors(pool, zone, rng) {
	const picked = [];
	for (const candidates of [pool.filter(c => zone && CONTINENTS[c.code] === zone), pool]) {
		const left = candidates.filter(c => !picked.some(p => p.code === c.code));
		while (picked.length < 3 && left.length) {
			const [pick] = left.splice(Math.floor(rng() * left.length), 1);
			picked.push({ code: pick.code, name: pick.name });
		}
		if (picked.length >= 3) break;
	}
	return picked;
}

function shuffleInPlace(array, rng) {
	for (let i = array.length - 1; i > 0; i--) {
		const j = Math.floor(rng() * (i + 1));
		[array[i], array[j]] = [array[j], array[i]];
	}
}
